import queryString from 'query-string'
import {
  formatDistanceToNow,
  format,
  subDays,
  subWeeks,
  subMonths,
  addDays,
  addWeeks,
  addMonths,
} from 'date-fns'
import { log } from 'src/services/app/log.service'
import { notifyMsgs, notifyService } from 'src/services/app/notify.service'
import { validateUrlProtocol } from 'src/services/helpers/security.helper'

export function isObjectEmpty(obj) {
  if (!obj || typeof obj !== 'object') return false
  return Object.keys(obj).length === 0
}

export function isValidEmail(value) {
  if (typeof value !== 'string') return false
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

export function getFileExtension(filename) {
  const lastDot = filename.lastIndexOf('.')
  if (lastDot === -1) return ''
  return filename.substring(lastDot).toLowerCase()
}

export function getFileTypeIcon(contentType) {
  if (!contentType) {
    return 'file-x-corner'
  }

  // Handle special cases
  const specialMimeTypes = {
    'application/pdf': 'file-text', // PDF uses file-text icon
    'application/zip': 'archive',
    'application/x-rar-compressed': 'archive',
    'application/x-7z-compressed': 'archive',
    'application/x-tar': 'archive',
    'application/gzip': 'archive',
    'application/json': 'file-json',
    'text/csv': 'file-spreadsheet',
  }

  if (specialMimeTypes[contentType]) {
    return specialMimeTypes[contentType]
  }

  // Extract MIME type prefix
  const mimePrefix = contentType.split('/')[0]

  // Map MIME type prefixes to icons
  const mimePrefixMap = {
    image: 'file-image',
    video: 'file-video',
    audio: 'file-audio',
    text: 'file-text',
    application: 'file-text',
  }

  return mimePrefixMap[mimePrefix] || 'file-text'
}

// Return null if not a boolean
export function parseBoolean(value) {
  // Handle null, undefined, or non-boolean/non-string types
  if (value === null || value === undefined) {
    return null
  }

  // Handle string values
  if (typeof value === 'string') {
    const lowercaseValue = value.toLowerCase()
    return lowercaseValue === 'true' || lowercaseValue === '1' || lowercaseValue === 'yes'
  }

  // Handle boolean values
  if (typeof value === 'boolean') {
    return value
  }

  // Any other type returns false
  return null
}

export const formatDateDistance = (dateString, options = { addSuffix: true }) => {
  try {
    const distance = formatDistanceToNow(new Date(dateString), options)
    if (distance === 'less than a minute ago') {
      return 'Just now'
    }
    return distance
  } catch {
    return 'Unknown'
  }
}

export const formatDateByFormat = (dateString, dateFormat = 'MMM d') => {
  try {
    return format(new Date(dateString), dateFormat)
  } catch {
    return 'Unknown'
  }
}

// Common filter and sort utilities

/**
 * Creates a filter function from query parameters
 * Uses query-string to sanitize by re-parsing the query params
 * @param {Object} defaultFilterFields - Default filter fields from constants
 * @param {Object} query - Query parameters from router
 * @returns {Object} - Filter object
 */
export function getFilterFromQuery(defaultFilterFields, query) {
  // Sanitize query params by round-tripping through query-string
  const sanitizedQuery = queryString.parse(queryString.stringify(query))
  const filterBy = {}
  Object.entries(defaultFilterFields).forEach(([key, defaultValue]) => {
    const value = sanitizedQuery[key] || defaultValue
    if (value !== undefined) {
      if (value === 'true' || value === 'false') {
        filterBy[key] = parseBoolean(value)
      } else {
        filterBy[key] = value
      }
    }
  })
  return filterBy
}

/**
 * Creates sort and pagination parameters from query
 * Uses query-string to sanitize by re-parsing the query params
 * @param {Object} defaultSortFields - Default sort fields from constants
 * @param {Object} query - Query parameters from router
 * @param {Object} options - Optional clamp options
 * @param {number} [options.totalItems] - When set, clamps page to valid range
 * @param {number} [options.rowsPerPage] - Override rowsPerPage from query
 * @returns {Object} - Sort and pagination object
 */
export function getSortAndPaginationFromQuery(
  defaultSortFields,
  query = {},
  { totalItems, rowsPerPage: rowsPerPageOverride } = {}
) {
  // Sanitize query params by round-tripping through query-string
  const sanitizedQuery = queryString.parse(queryString.stringify(query))

  const sortBy = { ...defaultSortFields }

  let descending = parseBoolean(sanitizedQuery.descending)
  if (descending === null) {
    descending = sortBy.descending
  }

  const rowsPerPage =
    rowsPerPageOverride ?? (parseInt(sanitizedQuery.rowsPerPage) || sortBy.rowsPerPage)
  const parsedPage = parseInt(sanitizedQuery.page) || sortBy.page
  const page =
    totalItems !== undefined ? clampTablePage(parsedPage, totalItems, rowsPerPage) : parsedPage

  return {
    sortBy: sanitizedQuery.sortBy || sortBy.sortBy,
    descending,
    page,
    rowsPerPage,
  }
}

export function isSameTableState(tableState1, tableState2) {
  return (
    tableState1.sortBy === tableState2.sortBy &&
    tableState1.descending === tableState2.descending &&
    tableState1.page === tableState2.page &&
    tableState1.rowsPerPage === tableState2.rowsPerPage
  )
}

export function isDateValue(value) {
  if (!value || typeof value !== 'string') return false
  const date = new Date(value)
  return !isNaN(date.getTime())
}

// Due dates are UTC calendar days; a local Date for that day compares and formats the same everywhere.
export function toUtcDay(value) {
  const date = new Date(value)
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

/**
 * Generic sort function for data arrays
 * @param {Array} items - Array of items to sort
 * @param {String} sortBy - Field to sort by
 * @param {Boolean} descending - Sort in descending order
 * @returns {Array} - Sorted array
 */
export function sortItems(items, sortBy, descending) {
  const sorted = [...items].sort((a, b) => {
    const aVal = a[sortBy] ?? ''
    const bVal = b[sortBy] ?? ''

    // Handle date fields
    if (isDateValue(aVal) && isDateValue(bVal)) {
      return new Date(aVal) - new Date(bVal)
    }

    // Handle string comparison (case-insensitive, locale-aware)
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal, undefined, { sensitivity: 'base' })
    }

    // Handle number comparison
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return aVal - bVal
    }

    // Default comparison for mixed types
    return aVal < bVal ? -1 : aVal > bVal ? 1 : 0
  })

  return descending ? sorted.reverse() : sorted
}

/**
 * Paginate an array of items
 * @param {Array} items - Array of items to paginate
 * @param {Number} page - Current page (1-based)
 * @param {Number} rowsPerPage - Number of items per page
 * @returns {Array} - Paginated array
 */
export function paginateItems(items, page, rowsPerPage) {
  const start = (page - 1) * rowsPerPage
  const end = start + rowsPerPage
  return items.slice(start, end)
}

/**
 * Clamp page to valid range for client-side pagination.
 * @param {number} page
 * @param {number} totalItems
 * @param {number} rowsPerPage
 * @returns {number}
 */
function clampTablePage(page, totalItems, rowsPerPage) {
  const parsed = Number.parseInt(page, 10)
  const normalizedPage = Number.isFinite(parsed) && parsed > 0 ? parsed : 1

  if (totalItems <= 0 || rowsPerPage <= 0) return 1

  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage))
  return Math.min(normalizedPage, totalPages)
}

/**
 * Calculate cutoff date based on date range
 * @param {String} dateRange - Date range string ('1d', '1w', '1m', '3m')
 * @param {Boolean} future - If true, calculate future date, otherwise past date
 * @returns {Date|null} - Cutoff date or null if no range
 */
export function getCutoffDate(dateRange, future = false) {
  if (!dateRange) return null

  const now = new Date()

  // Choose the appropriate date functions based on direction
  const dayFn = future ? addDays : subDays
  const weekFn = future ? addWeeks : subWeeks
  const monthFn = future ? addMonths : subMonths

  switch (dateRange) {
    case '1d':
      return dayFn(now, 1)
    case '1w':
      return weekFn(now, 1)
    case '1m':
      return monthFn(now, 1)
    case '3m':
      return monthFn(now, 3)
    case '6m':
      return monthFn(now, 6)
    case '1y':
      return monthFn(now, 12)
    default:
      return null
  }
}

/**
 * Sanitize query parameters
 * @param {Object} params - Query parameters to sanitize
 * @param {Object} defaults - Default query parameters
 * @returns {Object} - Sanitized query parameters
 */

export function sanitizeQuery(params, defaults) {
  params = { ...params }
  Object.entries(params).forEach(([key, value]) => {
    if (value === defaults[key]) {
      delete params[key]
    }
  })

  const cleanQueryString = queryString.stringify(params, {
    skipNull: true,
    skipEmptyString: true,
  })
  return queryString.parse(cleanQueryString)
}

export function normalizeWebsite(website) {
  if (!website) return ''
  // Handle array case - take first element if it's an array
  if (Array.isArray(website)) {
    return website.length > 0 ? website[0] : ''
  }
  return website
}

function hasWebsiteHostname(normalizedUrl) {
  try {
    const { hostname } = new URL(normalizedUrl)
    const labels = hostname.split('.')
    const tld = labels[labels.length - 1]

    return (
      labels.length >= 2 &&
      labels.every((label) => label.length > 0) &&
      tld.length >= 2 &&
      /^[a-z0-9-]+$/i.test(tld) &&
      /^[a-z0-9.-]+$/i.test(hostname)
    )
  } catch {
    return false
  }
}

export function formatWebsiteUrl(website) {
  const normalizedWebsite = normalizeWebsite(website)
  if (!normalizedWebsite) return ''

  const validation = validateUrlProtocol(normalizedWebsite)
  if (!validation.isValid || !hasWebsiteHostname(validation.normalizedUrl)) return ''
  return validation.normalizedUrl
}

export function isOptionalWebsiteValid(website) {
  const trimmedWebsite = typeof website === 'string' ? website.trim() : ''
  if (trimmedWebsite === '') return true
  return formatWebsiteUrl(trimmedWebsite) !== ''
}

export function extractDomainFromUrl(url) {
  if (!url || !url?.length) return ''

  let domain = url

  // stripping protocol, www, path, and query params
  domain = domain.replace(/^https?:\/\//, '')
  domain = domain.replace(/^www\./, '')
  domain = domain.split('/')[0]
  domain = domain.split('?')[0]

  // Check for invalid characters (spaces, special chars that aren't valid in domains)
  if (
    !domain ||
    domain.length < 2 ||
    domain.includes(' ') ||
    (!domain.includes('.') && domain.length < 3) ||
    !/^[a-zA-Z0-9.-]+$/.test(domain)
  ) {
    return ''
  }

  return domain
}

/**
 * Open URL in new tab with protocol validation
 * @param {string} url - URL to open
 * @description Validates URL protocol to prevent javascript:, data:, and other dangerous protocols
 */
export function openUrlInNewTab(url) {
  const validation = validateUrlProtocol(url)

  if (!validation.isValid) {
    log.error('openUrlInNewTab: URL validation failed', {
      url,
      error: validation.error,
    })
    notifyService.error(notifyMsgs.error.openFile)
    return
  }

  // Open the validated URL
  window.open(validation.normalizedUrl, '_blank', 'noopener,noreferrer')
}

export async function downloadFile(fileUrl, fileName) {
  const response = await fetch(fileUrl)
  const blob = await response.blob()
  const link = document.createElement('a')
  const blobFileUrl = window.URL.createObjectURL(blob)
  link.href = blobFileUrl
  const cleanFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_')
  link.download = cleanFileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(blobFileUrl)
}

/**
 * Get plural form of a string based on count
 * @param {String} singular - Singular form of the word
 * @param {Number} count - Number to determine pluralization
 * @param {Object} options - Options object
 * @param {String} options.plural - Custom plural form (if not provided, will be auto-generated)
 * @returns {String} - Singular or plural form based on count
 */
export function getPlural(singular, count, options = {}) {
  if (count === 1) {
    return singular
  }

  // Use custom plural form if provided
  if (options.plural) {
    return options.plural
  }

  // Auto-generate plural form based on basic English rules
  const lower = singular.toLowerCase()
  const lastChar = lower[lower.length - 1]
  const lastTwoChars = lower.slice(-2)

  // Words ending in s, x, z, ch, sh: add 'es'
  if (
    lastChar === 's' ||
    lastChar === 'x' ||
    lastChar === 'z' ||
    lastTwoChars === 'ch' ||
    lastTwoChars === 'sh'
  ) {
    return singular + 'es'
  }

  // Words ending in 'y' preceded by consonant: replace 'y' with 'ies'
  if (lastChar === 'y' && lower.length > 1) {
    const secondLastChar = lower[lower.length - 2]
    const vowels = ['a', 'e', 'i', 'o', 'u']
    if (!vowels.includes(secondLastChar)) {
      return singular.slice(0, -1) + 'ies'
    }
  }

  // Words ending in 'f': replace 'f' with 'ves'
  if (lastChar === 'f') {
    return singular.slice(0, -1) + 'ves'
  }

  // Words ending in 'fe': replace 'fe' with 'ves'
  if (lastTwoChars === 'fe') {
    return singular.slice(0, -2) + 'ves'
  }

  // Default: add 's'
  return singular + 's'
}

// Map stores palette *indexes* (not hex) so theme can swap light/dark palettes
const colorAssignments = new Map()

/**
 * Stable avatar fill for a user id from the active theme palette.
 * Caches index (not hex) so toggling light/dark keeps the same hue slot.
 * @param {string} userId
 * @param {string[]} colorPalette - Active theme's hex list (same length across themes)
 * @returns {string} Hex color
 */
export function getConsistentAvatarColor(userId, colorPalette) {
  if (!userId) {
    const randomIndex = Math.floor(Math.random() * colorPalette.length)
    return colorPalette[randomIndex]
  }

  if (!colorAssignments.has(userId)) {
    let hash = 0
    for (let i = 0; i < userId.length; i++) {
      const char = userId.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // Convert to 32-bit integer
    }
    colorAssignments.set(userId, Math.abs(hash) % colorPalette.length)
  }

  const index = colorAssignments.get(userId) % colorPalette.length
  return colorPalette[index]
}

export function getNameInitials(name) {
  if (!name) return 'U'
  const names = name
    .trim()
    .split(' ')
    .filter((n) => n.length > 0)
  if (names.length === 0) return 'U'
  if (names.length === 1) return names[0][0].toUpperCase()

  // Take first letter of first name and first letter of last name
  return (names[0][0] + names[names.length - 1][0]).toUpperCase()
}

/**
 * Wraps a mutation's mutateAsync function to catch errors and resolve with undefined
 * instead of rejecting. This prevents Vue warnings about unhandled promise rejections
 * when errors are already handled by the mutation's onError handler.
 *
 * @param {Function} mutateAsync - The mutateAsync function from a Vue Query mutation
 * @returns {Function} - Wrapped function that resolves on error instead of rejecting
 */
export function wrapMutationAsync(mutateAsync) {
  return async (...args) => {
    try {
      return await mutateAsync(...args)
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      // Error already handled by onError, resolve instead of rejecting
      // to prevent Vue warnings about unhandled promise rejections
      return undefined
    }
  }
}

/**
 * Finds the closest scrollable parent element by checking overflow styles.
 * @param {Element|null} el - Starting element
 * @returns {Element|Window} - The scrollable parent or window
 */
export function findScrollableParent(el) {
  while (el && el !== document.body) {
    const style = getComputedStyle(el)
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
      return el
    }
    el = el.parentElement
  }
  return window
}

/**
 * Compare two objects for deep equality using sorted JSON stringification
 * @param {Object} obj1 - First object to compare
 * @param {Object} obj2 - Second object to compare
 * @returns {Boolean} - True if objects are deeply equal
 */
export function areObjectsEqual(obj1, obj2) {
  if (obj1 === obj2) return true
  if (obj1 == null || obj2 == null) return false
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false
  if (Array.isArray(obj1) || Array.isArray(obj2)) return false

  const deepSort = (obj) => {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
      return obj
    }
    return Object.keys(obj)
      .sort()
      .reduce((result, key) => {
        result[key] = deepSort(obj[key])
        return result
      }, {})
  }

  return JSON.stringify(deepSort(obj1)) === JSON.stringify(deepSort(obj2))
}
